import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { track } from '../../../services/analytics/analytics'
import { EnergyLevelData, PTOSuggestionsResponse, PTOSuggestion } from '../types'
import { IGetLeaveTypeList } from '../../../types/custom-queries'

// Types for tracking events
interface BaseEventProperties {
  timestamp?: string
  session_id?: string
  [key: string]: string | string[] | number | boolean | undefined | null
}

interface MyEnergyLevelVisitedProperties extends BaseEventProperties {
  time_of_day: 'morning' | 'afternoon' | 'evening'
  day_of_week: string
  is_weekend: boolean
  is_mobile: boolean
  viewport_width: number
  viewport_height: number
  current_energy_score: number
  energy_mode: EnergyMode
  days_since_last_break: number
  has_trend_alert: boolean
  has_pto_suggestions: boolean
  department_comparison_available: boolean
  score_breakdown_factors_count: number
  referrer: string
  company_leave_types_count: number
  company_leave_types: string[]
}

interface MyEnergyLevelSessionCompleteProperties extends BaseEventProperties {
  session_duration_seconds: number
  scroll_depth_percentage: number
  interactions_count: number
  sections_viewed: string[]
  actions_taken: string[]
  buttons_clicked: string[]
  pto_suggestions_viewed: number
  pto_suggestions_clicked: number
  energy_trend_interactions: number
  score_breakdown_viewed: boolean
  exit_action: 'navigation' | 'close_tab' | 'timeout' | 'error'
  next_page?: string
  final_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelLoadTimeProperties extends BaseEventProperties {
  total_load_time_ms: number
  energy_data_load_time_ms: number
  pto_suggestions_load_time_ms: number
  first_paint_ms: number
  time_to_interactive_ms: number
  data_size_kb: number
  cached_data_used: boolean
  energy_gauge_loaded_ms: number
  trend_chart_loaded_ms: number
  suggestions_loaded_ms: number
  score_breakdown_loaded_ms: number
  connection_type: string
  browser_version: string
  is_slow_load: boolean
  had_layout_shift: boolean
  had_hydration_error: boolean
  current_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelErrorProperties extends BaseEventProperties {
  error_type: 'network_error' | 'api_error' | 'permission_error' | 'parse_error' | 'component_error' | 'energy_load_error'
  error_message: string
  error_code?: string
  section_affected: string
  user_action_before_error?: string
  time_since_page_load_ms: number
  has_cached_data: boolean
  retry_attempted: boolean
  auto_recovery_possible: boolean
  browser_version: string
  device_info: string
  connection_status: 'online' | 'offline' | 'slow'
  current_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelActionTakenProperties extends BaseEventProperties {
  action_type: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click'
  action_name: string
  element_type: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion'
  section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown'
  current_energy_score: number
  energy_mode: EnergyMode
  is_loading: boolean
  scroll_position: number
  time_since_page_load_ms: number
  led_to_navigation: boolean
  destination_url?: string
}

interface MyEnergyLevelPTOSuggestionInteractionProperties extends BaseEventProperties {
  interaction_type: 'viewed' | 'clicked' | 'expanded' | 'request_leave_clicked'
  suggestion_index: number
  suggestion_start_date: string
  suggestion_duration_days: number
  suggestion_pto_days_used: number
  predicted_energy_improvement: number
  improvement_percentage: number
  overlapping_users: number
  extended_by_holidays: boolean
  confidence_level: 'low' | 'medium' | 'high'
  is_best_suggestion: boolean
  time_in_view_ms?: number
  current_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelEnergyTrendInteractionProperties extends BaseEventProperties {
  interaction_type: 'hover' | 'click' | 'zoom' | 'filter_change'
  chart_section: 'personal_trend' | 'department_comparison' | 'both'
  data_point_date?: string
  data_point_score?: number
  comparison_enabled: boolean
  trend_direction: 'increasing' | 'decreasing' | 'stable'
  time_range_days: number
  current_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelScoreBreakdownViewedProperties extends BaseEventProperties {
  factors_count: number
  highest_factor: string
  highest_factor_score: number
  lowest_factor: string
  lowest_factor_score: number
  factors_viewed: string[]
  time_in_section_ms: number
  factor_clicked?: string
}

interface MyEnergyLevelEngagedProperties extends BaseEventProperties {
  milestone_type: 'time_spent' | 'interactions' | 'sections_explored'
  milestone_value: number
  current_section: string
  actions_completed: number
  data_freshness: 'fresh' | 'partial' | 'stale'
  scroll_depth: number
  unique_sections_visited: number
  meaningful_interactions: number
  energy_score_range: string
  current_energy_score: number
  energy_mode: EnergyMode
}

interface MyEnergyLevelAlertSeenProperties extends BaseEventProperties {
  alert_type: 'low_energy' | 'high_energy' | 'trend_warning' | 'no_break_warning' | 'department_comparison'
  alert_severity: 'info' | 'warning' | 'error'
  alert_message: string
  action: 'viewed' | 'clicked' | 'dismissed'
  time_visible_ms: number
  energy_score: number
  days_since_break: number
  led_to_action: boolean
  follow_up_action?: string
}

interface MyEnergyLevelTooltipViewedProperties extends BaseEventProperties {
  tooltip_type: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode'
  tooltip_content: string
  element_hovered: string
  section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown'
  hover_duration_ms: number
  current_energy_score: number
  energy_mode: EnergyMode
  tooltip_triggered_by: 'mouse_hover' | 'touch' | 'keyboard_focus'
  hovered_score?: number // The specific score that was hovered (for chart points)
  hovered_date?: string // The date of the hovered data point (for chart points)
  hovered_week?: number // The week number of the hovered data point (for chart points)
  hovered_year?: number // The year of the hovered data point (for chart points)
  chart_dataset?: string // Which dataset was hovered (personal vs department)
}

interface MyEnergyLevelBestSuggestionClickedProperties extends BaseEventProperties {
  suggestion_index: number
  suggestion_start_date: string
  suggestion_end_date: string
  suggestion_duration_days: number
  suggestion_pto_days_used: number
  predicted_energy_improvement: number
  improvement_percentage: number
  overlapping_users: number
  overlapping_user_names: string[]
  extended_by_holidays: boolean
  holidays_included?: string[]
  confidence_level: 'low' | 'medium' | 'high'
  current_energy_score: number
  energy_mode: EnergyMode
  leave_type_id?: string
  available_days?: number
  recommended_usage?: number
  click_source: 'suggestion_card' | 'quick_action' | 'detailed_view'
  rank_compared_to_other_suggestions: number
  total_suggestions_available: number
}

interface MyEnergyLevelRegularSuggestionClickedProperties extends BaseEventProperties {
  suggestion_index: number
  suggestion_start_date: string
  suggestion_end_date: string
  suggestion_duration_days: number
  suggestion_pto_days_used: number
  predicted_energy_improvement: number
  improvement_percentage: number
  overlapping_users: number
  overlapping_user_names: string[]
  extended_by_holidays: boolean
  holidays_included?: string[]
  confidence_level: 'low' | 'medium' | 'high'
  current_energy_score: number
  energy_mode: EnergyMode
  leave_type_id?: string
  available_days?: number
  recommended_usage?: number
  click_source: 'suggestion_card' | 'quick_action' | 'detailed_view'
  rank_compared_to_best: number
  total_suggestions_available: number
  improvement_vs_best_suggestion: number
}

interface MyEnergyLevelFeedbackLinkClickedProperties extends BaseEventProperties {
  feedback_link_location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state'
  current_energy_score: number
  energy_mode: EnergyMode
  days_since_last_break: number
  time_since_page_load_ms: number
  user_engagement_level: 'low' | 'medium' | 'high'
  sections_viewed_count: number
  interactions_count: number
  company_leave_types_count: number
  company_leave_types: string[]
}

interface MyEnergyLevelFeedbackSubmittedProperties extends BaseEventProperties {
  feedback_reason: string // Changed to accept any user-entered text instead of predefined categories
  user_suggested_score?: number
  current_calculated_score: number
  score_difference?: number
  energy_mode: EnergyMode
  reported_energy_mode?: EnergyMode
  days_since_last_break: number
  feedback_text_length: number
  has_custom_text: boolean
  time_to_submit_ms: number
  submission_method: 'quick_rating' | 'detailed_form' | 'inline_feedback'
  user_engagement_level: 'low' | 'medium' | 'high'
  sections_viewed_before_feedback: string[]
  allow_contact: boolean // Whether user agreed to be contacted about their feedback
  company_leave_types_count: number
  company_leave_types: string[]
}

interface MyEnergyLevelFeatureDiscoveredProperties extends BaseEventProperties {
  feature_name: 'energy_gauge' | 'trend_chart' | 'department_comparison' | 'pto_suggestions' | 'score_breakdown' | 'energy_prediction'
  discovery_method: 'hover' | 'click' | 'scroll' | 'tooltip'
  time_to_discovery_ms: number
  session_count: number
  feature_visible: boolean
  feature_relevant: boolean
  interacted_after_discovery: boolean
  time_spent_with_feature_ms: number
  current_energy_score: number
}

interface MyEnergyLevelEmptyStateCheckinProperties extends BaseEventProperties {
  checkin_type: 'pre_vacation' | 'post_vacation'
  selected_energy_mode: EnergyMode
  current_energy_score: number
  current_energy_mode: EnergyMode
  time_since_page_load_ms: number
}

interface MyEnergyLevelViewFullTeamCalendarClickedProperties extends BaseEventProperties {
  current_energy_score: number
  energy_mode: EnergyMode
  has_pto_suggestions: boolean
  pto_suggestions_count: number
  best_suggestion_duration?: number
  best_suggestion_improvement?: number
  days_since_last_break: number
  time_spent_on_page_ms: number
  sections_viewed_before_click: string[]
  click_source: 'suggestions_section'
  destination_url: string
}

// Session tracking state
class MyEnergyLevelSessionTracker {
  private sessionStartTime: number = Date.now()
  private sessionId = `energy_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private pageLoadTime: number = Date.now()
  private sectionsViewed: Set<string> = new Set()
  private actionsTaken: Set<string> = new Set()
  private buttonsClicked: Set<string> = new Set()
  private interactionCount = 0
  private maxScrollDepth = 0
  private ptoSuggestionsViewed = 0
  private ptoSuggestionsClicked = 0
  private energyTrendInteractions = 0
  private scoreBreakdownViewed = false

  // Store current energy data for use in engagement tracking
  private currentEnergyScore = 0
  private currentEnergyMode: EnergyMode = EnergyMode.FUNCTIONAL

  // Store timeout IDs and event handlers for cleanup
  private timeouts: NodeJS.Timeout[] = []
  private eventHandlers: { event: string; handler: () => void }[] = []

  // Helper methods
  private getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 17) return 'afternoon'
    return 'evening'
  }

  private getDayOfWeek(): string {
    return new Date().toLocaleDateString('en-US', { weekday: 'long' })
  }

  private isWeekend(): boolean {
    const day = new Date().getDay()
    return day === 0 || day === 6
  }

  private isMobile(): boolean {
    return window.innerWidth < 768
  }

  private getConnectionType(): string {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
    return connection?.effectiveType || 'unknown'
  }

  private getBrowserVersion(): string {
    const userAgent = navigator.userAgent
    if (userAgent.includes('Chrome')) return `Chrome/${userAgent.split('Chrome/')[1]?.split(' ')[0] || 'unknown'}`
    if (userAgent.includes('Firefox')) return `Firefox/${userAgent.split('Firefox/')[1]?.split(' ')[0] || 'unknown'}`
    if (userAgent.includes('Safari')) return `Safari/${userAgent.split('Version/')[1]?.split(' ')[0] || 'unknown'}`
    return 'unknown'
  }

  private getDeviceInfo(): string {
    const { platform, userAgent } = navigator
    return `${platform} - ${userAgent.substring(0, 100)}`
  }

  private getEnergyScoreRange(score: number): string {
    if (score < 20) return 'very-low'
    if (score < 40) return 'low'
    if (score < 60) return 'moderate'
    if (score < 80) return 'good'
    return 'excellent'
  }

  private getUserEngagementLevel(): 'low' | 'medium' | 'high' {
    const timeSpentMinutes = (Date.now() - this.sessionStartTime) / (1000 * 60)
    const sectionsViewed = this.sectionsViewed.size
    const interactions = this.interactionCount

    // High engagement: spent >2 minutes, viewed 3+ sections, 5+ interactions
    if (timeSpentMinutes > 2 && sectionsViewed >= 3 && interactions >= 5) {
      return 'high'
    }

    // Medium engagement: spent >30 seconds, viewed 2+ sections, 2+ interactions
    if (timeSpentMinutes > 0.5 && sectionsViewed >= 2 && interactions >= 2) {
      return 'medium'
    }

    return 'low'
  }

  private updateScrollDepth(): void {
    const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100)
    this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollDepth || 0)
  }

  // Update current energy data for engagement tracking
  updateEnergyData(energyScore: number, energyMode: EnergyMode): void {
    this.currentEnergyScore = energyScore
    this.currentEnergyMode = energyMode
  }

  // Public tracking methods
  trackVisited(energyData?: EnergyLevelData, ptoSuggestions?: PTOSuggestionsResponse, leaveTypes?: IGetLeaveTypeList[]): void {
    const leaveTypeNames = leaveTypes?.map(lt => lt.name) || []

    const properties: MyEnergyLevelVisitedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_of_day: this.getTimeOfDay(),
      day_of_week: this.getDayOfWeek(),
      is_weekend: this.isWeekend(),
      is_mobile: this.isMobile(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      current_energy_score: energyData?.currentScore.current || 0,
      energy_mode: energyData?.energyMode as EnergyMode,
      days_since_last_break: energyData?.daysSinceLastBreak || 0,
      has_trend_alert: !!energyData?.trendAlert,
      has_pto_suggestions: (ptoSuggestions?.suggestions?.length || 0) > 0,
      department_comparison_available: (energyData?.departmentTrend?.length || 0) > 0,
      score_breakdown_factors_count: energyData?.scoreBreakdown?.length || 0,
      referrer: document.referrer || 'direct',
      company_leave_types_count: leaveTypes?.length || 0,
      company_leave_types: leaveTypeNames,
    }

    track('MY_ENERGY_LEVEL_VISITED', properties)
  }

  trackSessionComplete(exitAction: 'navigation' | 'close_tab' | 'timeout' | 'error' = 'navigation',
    nextPage?: string, energyData?: { currentScore: number; energyMode: EnergyMode }): void {
    this.updateScrollDepth()

    const properties: MyEnergyLevelSessionCompleteProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      session_duration_seconds: Math.round((Date.now() - this.sessionStartTime) / 1000),
      scroll_depth_percentage: this.maxScrollDepth,
      interactions_count: this.interactionCount,
      sections_viewed: Array.from(this.sectionsViewed),
      actions_taken: Array.from(this.actionsTaken),
      buttons_clicked: Array.from(this.buttonsClicked),
      pto_suggestions_viewed: this.ptoSuggestionsViewed,
      pto_suggestions_clicked: this.ptoSuggestionsClicked,
      energy_trend_interactions: this.energyTrendInteractions,
      score_breakdown_viewed: this.scoreBreakdownViewed,
      exit_action: exitAction,
      next_page: nextPage,
      final_energy_score: energyData?.currentScore || 0,
      energy_mode: energyData?.energyMode as EnergyMode,
    }

    track('MY_ENERGY_LEVEL_SESSION_COMPLETE', properties)
  }

  trackLoadTime(performanceData: {
    totalLoadTime: number
    energyDataLoadTime: number
    ptoSuggestionsLoadTime: number
    firstPaintTime?: number
    timeToInteractive?: number
    dataSize?: number
    cachedDataUsed?: boolean
    componentLoadTimes?: {
      energyGauge?: number
      trendChart?: number
      suggestions?: number
      scoreBreakdown?: number
    }
  }): void {
    const properties: MyEnergyLevelLoadTimeProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      total_load_time_ms: performanceData.totalLoadTime,
      energy_data_load_time_ms: performanceData.energyDataLoadTime,
      pto_suggestions_load_time_ms: performanceData.ptoSuggestionsLoadTime,
      first_paint_ms: performanceData.firstPaintTime || 0,
      time_to_interactive_ms: performanceData.timeToInteractive || 0,
      data_size_kb: performanceData.dataSize || 0,
      cached_data_used: performanceData.cachedDataUsed || false,
      energy_gauge_loaded_ms: performanceData.componentLoadTimes?.energyGauge || 0,
      trend_chart_loaded_ms: performanceData.componentLoadTimes?.trendChart || 0,
      suggestions_loaded_ms: performanceData.componentLoadTimes?.suggestions || 0,
      score_breakdown_loaded_ms: performanceData.componentLoadTimes?.scoreBreakdown || 0,
      connection_type: this.getConnectionType(),
      browser_version: this.getBrowserVersion(),
      is_slow_load: performanceData.totalLoadTime > 3000,
      had_layout_shift: false, // Could be enhanced with actual CLS measurement
      had_hydration_error: false,
      current_energy_score: this.currentEnergyScore,
      energy_mode: this.currentEnergyMode,
    }

    track('MY_ENERGY_LEVEL_LOAD_TIME', properties)
  }

  trackError(errorData: {
    errorType: 'network_error' | 'api_error' | 'permission_error' | 'parse_error' | 'component_error' | 'energy_load_error'
    errorMessage: string
    errorCode?: string
    sectionAffected: string
    userActionBeforeError?: string
    hasCachedData?: boolean
    retryAttempted?: boolean
    autoRecoveryPossible?: boolean
  }): void {
    const properties: MyEnergyLevelErrorProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      error_type: errorData.errorType,
      error_message: errorData.errorMessage,
      error_code: errorData.errorCode,
      section_affected: errorData.sectionAffected,
      user_action_before_error: errorData.userActionBeforeError,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      has_cached_data: errorData.hasCachedData || false,
      retry_attempted: errorData.retryAttempted || false,
      auto_recovery_possible: errorData.autoRecoveryPossible || false,
      browser_version: this.getBrowserVersion(),
      device_info: this.getDeviceInfo(),
      connection_status: navigator.onLine ? 'online' : 'offline',
      current_energy_score: this.currentEnergyScore,
      energy_mode: this.currentEnergyMode,
    }

    track('MY_ENERGY_LEVEL_ERROR', properties)
  }

  trackActionTaken(actionData: {
    actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click'
    actionName: string
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion'
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown'
    currentEnergyScore: number
    energyMode: EnergyMode
    isLoading?: boolean
    ledToNavigation?: boolean
    destinationUrl?: string
  }): void {
    this.interactionCount++
    this.actionsTaken.add(actionData.actionName)
    this.sectionsViewed.add(actionData.section)
    this.updateScrollDepth()

    if (actionData.actionType === 'button_click') {
      this.buttonsClicked.add(actionData.actionName)
    }

    if (actionData.section === 'trend_chart') {
      this.energyTrendInteractions++
    }

    const properties: MyEnergyLevelActionTakenProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      action_type: actionData.actionType,
      action_name: actionData.actionName,
      element_type: actionData.elementType,
      section: actionData.section,
      current_energy_score: actionData.currentEnergyScore,
      energy_mode: actionData.energyMode,
      is_loading: actionData.isLoading || false,
      scroll_position: window.scrollY,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      led_to_navigation: actionData.ledToNavigation || false,
      destination_url: actionData.destinationUrl,
    }

    track('MY_ENERGY_LEVEL_ACTION_TAKEN', properties)
  }

  trackPTOSuggestionInteraction(interactionData: {
    interactionType: 'viewed' | 'clicked' | 'expanded' | 'request_leave_clicked'
    suggestion: PTOSuggestion
    suggestionIndex: number
    isBestSuggestion: boolean
    timeInViewMs?: number
  }): void {
    this.sectionsViewed.add('suggestions')

    if (interactionData.interactionType === 'viewed') {
      this.ptoSuggestionsViewed++
    } else if (interactionData.interactionType === 'clicked' || interactionData.interactionType === 'request_leave_clicked') {
      this.ptoSuggestionsClicked++
    }

    const properties: MyEnergyLevelPTOSuggestionInteractionProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      interaction_type: interactionData.interactionType,
      suggestion_index: interactionData.suggestionIndex,
      suggestion_start_date: interactionData.suggestion.startDate,
      suggestion_duration_days: interactionData.suggestion.durationDays,
      suggestion_pto_days_used: interactionData.suggestion.ptoDaysUsed,
      predicted_energy_improvement: interactionData.suggestion.energyPrediction.improvement,
      improvement_percentage: interactionData.suggestion.energyPrediction.improvementPercentage,
      overlapping_users: interactionData.suggestion.overlappingUsers,
      extended_by_holidays: interactionData.suggestion.extendedByHolidays,
      confidence_level: interactionData.suggestion.energyPrediction.confidence,
      is_best_suggestion: interactionData.isBestSuggestion,
      time_in_view_ms: interactionData.timeInViewMs,
      current_energy_score: this.currentEnergyScore,
      energy_mode: this.currentEnergyMode,
    }

    track('MY_ENERGY_LEVEL_PTO_SUGGESTION_INTERACTION', properties)
  }

  trackEnergyTrendInteraction(interactionData: {
    interactionType: 'hover' | 'click' | 'zoom' | 'filter_change'
    chartSection: 'personal_trend' | 'department_comparison' | 'both'
    dataPointDate?: string
    dataPointScore?: number
    comparisonEnabled: boolean
    trendDirection: 'increasing' | 'decreasing' | 'stable'
    timeRangeDays: number
  }): void {
    this.sectionsViewed.add('trend_chart')
    this.energyTrendInteractions++

    const properties: MyEnergyLevelEnergyTrendInteractionProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      interaction_type: interactionData.interactionType,
      chart_section: interactionData.chartSection,
      data_point_date: interactionData.dataPointDate,
      data_point_score: interactionData.dataPointScore,
      comparison_enabled: interactionData.comparisonEnabled,
      trend_direction: interactionData.trendDirection,
      time_range_days: interactionData.timeRangeDays,
      current_energy_score: this.currentEnergyScore,
      energy_mode: this.currentEnergyMode,
    }

    track('MY_ENERGY_LEVEL_ENERGY_TREND_INTERACTION', properties)
  }

  trackScoreBreakdownViewed(breakdownData: {
    factorsCount: number
    highestFactor: string
    highestFactorScore: number
    lowestFactor: string
    lowestFactorScore: number
    factorsViewed: string[]
    timeInSectionMs: number
    factorClicked?: string
  }): void {
    this.sectionsViewed.add('score_breakdown')
    this.scoreBreakdownViewed = true

    const properties: MyEnergyLevelScoreBreakdownViewedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      factors_count: breakdownData.factorsCount,
      highest_factor: breakdownData.highestFactor,
      highest_factor_score: breakdownData.highestFactorScore,
      lowest_factor: breakdownData.lowestFactor,
      lowest_factor_score: breakdownData.lowestFactorScore,
      factors_viewed: breakdownData.factorsViewed,
      time_in_section_ms: breakdownData.timeInSectionMs,
      factor_clicked: breakdownData.factorClicked,
    }

    track('MY_ENERGY_LEVEL_SCORE_BREAKDOWN_VIEWED', properties)
  }

  trackEngaged(milestoneData: {
    milestoneType: 'time_spent' | 'interactions' | 'sections_explored'
    milestoneValue: number
    currentSection: string
    actionsCompleted: number
    dataFreshness: 'fresh' | 'partial' | 'stale'
    energyScore: number
    energyMode: EnergyMode
  }): void {
    this.updateScrollDepth()

    const properties: MyEnergyLevelEngagedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      milestone_type: milestoneData.milestoneType,
      milestone_value: milestoneData.milestoneValue,
      current_section: milestoneData.currentSection,
      actions_completed: milestoneData.actionsCompleted,
      data_freshness: milestoneData.dataFreshness,
      scroll_depth: this.maxScrollDepth,
      unique_sections_visited: this.sectionsViewed.size,
      meaningful_interactions: this.interactionCount,
      energy_score_range: this.getEnergyScoreRange(milestoneData.energyScore),
      current_energy_score: milestoneData.energyScore,
      energy_mode: milestoneData.energyMode,
    }

    track('MY_ENERGY_LEVEL_ENGAGED', properties)
  }

  trackAlertSeen(alertData: {
    alertType: 'low_energy' | 'high_energy' | 'trend_warning' | 'no_break_warning' | 'department_comparison'
    alertSeverity: 'info' | 'warning' | 'error'
    alertMessage: string
    action: 'viewed' | 'clicked' | 'dismissed'
    timeVisibleMs: number
    energyScore: number
    daysSinceBreak: number
    ledToAction?: boolean
    followUpAction?: string
  }): void {
    const properties: MyEnergyLevelAlertSeenProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      alert_type: alertData.alertType,
      alert_severity: alertData.alertSeverity,
      alert_message: alertData.alertMessage,
      action: alertData.action,
      time_visible_ms: alertData.timeVisibleMs,
      energy_score: alertData.energyScore,
      days_since_break: alertData.daysSinceBreak,
      led_to_action: alertData.ledToAction || false,
      follow_up_action: alertData.followUpAction,
    }

    track('MY_ENERGY_LEVEL_ALERT_SEEN', properties)
  }

  trackTooltipViewed(tooltipData: {
    tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode'
    tooltipContent: string
    elementHovered: string
    section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown'
    hoverDurationMs: number
    currentEnergyScore: number
    energyMode: EnergyMode
    tooltipTriggeredBy?: 'mouse_hover' | 'touch' | 'keyboard_focus'
    hoveredScore?: number
    hoveredDate?: string
    hoveredWeek?: number
    hoveredYear?: number
    chartDataset?: string
  }): void {
    this.sectionsViewed.add(tooltipData.section)
    this.interactionCount++

    // Determine event name based on section
    const getEventName = (section: string): string => {
      switch (section) {
        case 'trend_chart':
          return 'MY_ENERGY_LEVEL_CHART_TOOLTIP_VIEWED'
        case 'score_breakdown':
          return 'MY_ENERGY_LEVEL_SCORE_BREAKDOWN_TOOLTIP_VIEWED'
        case 'energy_gauge':
          return 'MY_ENERGY_LEVEL_GAUGE_TOOLTIP_VIEWED'
        case 'suggestions':
          return 'MY_ENERGY_LEVEL_OVERLAPPING_TEAMMATES_TOOLTIP_VIEWED'
        default:
          return 'MY_ENERGY_LEVEL_TOOLTIP_VIEWED'
      }
    }

    const eventName = getEventName(tooltipData.section)

    const properties: MyEnergyLevelTooltipViewedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      tooltip_type: tooltipData.tooltipType,
      tooltip_content: tooltipData.tooltipContent,
      element_hovered: tooltipData.elementHovered,
      section: tooltipData.section,
      hover_duration_ms: tooltipData.hoverDurationMs,
      current_energy_score: tooltipData.currentEnergyScore,
      energy_mode: tooltipData.energyMode,
      tooltip_triggered_by: tooltipData.tooltipTriggeredBy || 'mouse_hover',
      hovered_score: tooltipData.hoveredScore,
      hovered_date: tooltipData.hoveredDate,
      hovered_week: tooltipData.hoveredWeek,
      hovered_year: tooltipData.hoveredYear,
      chart_dataset: tooltipData.chartDataset,
    }

    track(eventName, properties)
  }

  trackViewFullTeamCalendarClicked(calendarData: {
    currentEnergyScore: number
    energyMode: EnergyMode
    hasPtoSuggestions: boolean
    ptoSuggestionsCount: number
    bestSuggestionDuration?: number
    bestSuggestionImprovement?: number
    daysSinceLastBreak: number
    clickSource: 'suggestions_section'
    destinationUrl: string
  }): void {
    this.sectionsViewed.add('suggestions')
    this.interactionCount++
    this.actionsTaken.add('view_full_team_calendar')

    const properties: MyEnergyLevelViewFullTeamCalendarClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      current_energy_score: calendarData.currentEnergyScore,
      energy_mode: calendarData.energyMode,
      has_pto_suggestions: calendarData.hasPtoSuggestions,
      pto_suggestions_count: calendarData.ptoSuggestionsCount,
      best_suggestion_duration: calendarData.bestSuggestionDuration,
      best_suggestion_improvement: calendarData.bestSuggestionImprovement,
      days_since_last_break: calendarData.daysSinceLastBreak,
      time_spent_on_page_ms: Date.now() - this.sessionStartTime,
      sections_viewed_before_click: Array.from(this.sectionsViewed),
      click_source: calendarData.clickSource,
      destination_url: calendarData.destinationUrl,
    }

    track('MY_ENERGY_LEVEL_VIEW_FULL_TEAM_CALENDAR_CLICKED', properties)
  }

  trackSuggestedLeaveClicked(suggestionData: {
    suggestion: PTOSuggestion
    suggestionIndex: number
    isBestSuggestion: boolean
    currentEnergyScore: number
    energyMode: EnergyMode
    clickSource?: 'suggestion_card' | 'quick_action' | 'detailed_view'
    totalSuggestions?: number
    bestSuggestion?: PTOSuggestion
  }): void {
    this.sectionsViewed.add('suggestions')
    this.actionsTaken.add('suggested_leave_clicked')
    this.buttonsClicked.add('take_suggested_leave')
    this.interactionCount++
    this.ptoSuggestionsClicked++

    if (suggestionData.isBestSuggestion) {
      // Track best suggestion click
      const properties: MyEnergyLevelBestSuggestionClickedProperties = {
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
        suggestion_index: suggestionData.suggestionIndex,
        suggestion_start_date: suggestionData.suggestion.startDate,
        suggestion_end_date: suggestionData.suggestion.endDate,
        suggestion_duration_days: suggestionData.suggestion.durationDays,
        suggestion_pto_days_used: suggestionData.suggestion.ptoDaysUsed,
        predicted_energy_improvement: suggestionData.suggestion.energyPrediction.improvement,
        improvement_percentage: suggestionData.suggestion.energyPrediction.improvementPercentage,
        overlapping_users: suggestionData.suggestion.overlappingUsers,
        overlapping_user_names: suggestionData.suggestion.overlappingUserNames,
        extended_by_holidays: suggestionData.suggestion.extendedByHolidays,
        holidays_included: suggestionData.suggestion.holidaysIncluded?.map(x => x.name),
        confidence_level: suggestionData.suggestion.energyPrediction.confidence,
        current_energy_score: suggestionData.currentEnergyScore,
        energy_mode: suggestionData.energyMode,
        leave_type_id: suggestionData.suggestion.leaveTypeId,
        available_days: suggestionData.suggestion.availableDays,
        recommended_usage: suggestionData.suggestion.recommendedUsage,
        click_source: suggestionData.clickSource || 'suggestion_card',
        rank_compared_to_other_suggestions: 1, // Best is always rank 1
        total_suggestions_available: suggestionData.totalSuggestions || 1,
      }

      track('MY_ENERGY_LEVEL_BEST_SUGGESTION_CLICKED', properties)
    } else {
      // Track regular suggestion click
      const improvementVsBest = suggestionData.bestSuggestion
        ? suggestionData.suggestion.energyPrediction.improvement - suggestionData.bestSuggestion.energyPrediction.improvement
        : 0

      const properties: MyEnergyLevelRegularSuggestionClickedProperties = {
        timestamp: new Date().toISOString(),
        session_id: this.sessionId,
        suggestion_index: suggestionData.suggestionIndex,
        suggestion_start_date: suggestionData.suggestion.startDate,
        suggestion_end_date: suggestionData.suggestion.endDate,
        suggestion_duration_days: suggestionData.suggestion.durationDays,
        suggestion_pto_days_used: suggestionData.suggestion.ptoDaysUsed,
        predicted_energy_improvement: suggestionData.suggestion.energyPrediction.improvement,
        improvement_percentage: suggestionData.suggestion.energyPrediction.improvementPercentage,
        overlapping_users: suggestionData.suggestion.overlappingUsers,
        overlapping_user_names: suggestionData.suggestion.overlappingUserNames,
        extended_by_holidays: suggestionData.suggestion.extendedByHolidays,
        holidays_included: suggestionData.suggestion.holidaysIncluded?.map(x => x.name),
        confidence_level: suggestionData.suggestion.energyPrediction.confidence,
        current_energy_score: suggestionData.currentEnergyScore,
        energy_mode: suggestionData.energyMode,
        leave_type_id: suggestionData.suggestion.leaveTypeId,
        available_days: suggestionData.suggestion.availableDays,
        recommended_usage: suggestionData.suggestion.recommendedUsage,
        click_source: suggestionData.clickSource || 'suggestion_card',
        rank_compared_to_best: suggestionData.suggestionIndex, // Index represents rank
        total_suggestions_available: suggestionData.totalSuggestions || 1,
        improvement_vs_best_suggestion: improvementVsBest,
      }

      track('MY_ENERGY_LEVEL_REGULAR_SUGGESTION_CLICKED', properties)
    }
  }

  trackFeedbackLinkClicked(feedbackData: {
    feedbackLinkLocation: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state'
    currentEnergyScore: number
    energyMode: EnergyMode
    daysSinceLastBreak: number
    leaveTypes?: IGetLeaveTypeList[]
  }): void {
    this.interactionCount++
    this.actionsTaken.add('feedback_link_clicked')
    this.buttonsClicked.add('feedback_link')

    const leaveTypeNames = feedbackData.leaveTypes?.map(lt => lt.name) || []

    const properties: MyEnergyLevelFeedbackLinkClickedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      feedback_link_location: feedbackData.feedbackLinkLocation,
      current_energy_score: feedbackData.currentEnergyScore,
      energy_mode: feedbackData.energyMode,
      days_since_last_break: feedbackData.daysSinceLastBreak,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      user_engagement_level: this.getUserEngagementLevel(),
      sections_viewed_count: this.sectionsViewed.size,
      interactions_count: this.interactionCount,
      company_leave_types_count: feedbackData.leaveTypes?.length || 0,
      company_leave_types: leaveTypeNames,
    }

    track('MY_ENERGY_LEVEL_FEEDBACK_LINK_CLICKED', properties)
  }

  trackFeedbackSubmitted(feedbackData: {
    feedbackReason: string // Changed to accept any user-entered text
    userSuggestedScore?: number
    currentCalculatedScore: number
    energyMode: EnergyMode
    daysSinceLastBreak: number
    feedbackText: string
    timeToSubmitMs: number
    reportedEnergyMode?: EnergyMode
    submissionMethod?: 'quick_rating' | 'detailed_form' | 'inline_feedback'
    allowContact: boolean // Whether user agreed to be contacted about their feedback
    leaveTypes?: IGetLeaveTypeList[]
  }): void {
    this.interactionCount++
    this.actionsTaken.add('feedback_submitted')

    const scoreDifference = feedbackData.userSuggestedScore
      ? feedbackData.userSuggestedScore - feedbackData.currentCalculatedScore
      : undefined

    const leaveTypeNames = feedbackData.leaveTypes?.map(lt => lt.name) || []

    const properties: MyEnergyLevelFeedbackSubmittedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      feedback_reason: feedbackData.feedbackReason,
      user_suggested_score: feedbackData.userSuggestedScore,
      current_calculated_score: feedbackData.currentCalculatedScore,
      score_difference: scoreDifference,
      energy_mode: feedbackData.energyMode,
      days_since_last_break: feedbackData.daysSinceLastBreak,
      feedback_text_length: feedbackData.feedbackText.length,
      has_custom_text: feedbackData.feedbackText.trim().length > 0,
      time_to_submit_ms: feedbackData.timeToSubmitMs,
      submission_method: feedbackData.submissionMethod || 'detailed_form',
      user_engagement_level: this.getUserEngagementLevel(),
      sections_viewed_before_feedback: Array.from(this.sectionsViewed),
      allow_contact: feedbackData.allowContact,
      company_leave_types_count: feedbackData.leaveTypes?.length || 0,
      company_leave_types: leaveTypeNames,
      reported_energy_mode: feedbackData.reportedEnergyMode,
    }

    track('MY_ENERGY_LEVEL_FEEDBACK_SUBMITTED', properties)
  }

  trackFeatureDiscovered(featureData: {
    featureName: 'energy_gauge' | 'trend_chart' | 'department_comparison' | 'pto_suggestions' | 'score_breakdown' | 'energy_prediction'
    discoveryMethod: 'hover' | 'click' | 'scroll' | 'tooltip'
    timeToDiscoveryMs: number
    sessionCount: number
    featureVisible: boolean
    featureRelevant: boolean
    interactedAfterDiscovery: boolean
    timeSpentWithFeatureMs: number
    currentEnergyScore: number
  }): void {
    const properties: MyEnergyLevelFeatureDiscoveredProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      feature_name: featureData.featureName,
      discovery_method: featureData.discoveryMethod,
      time_to_discovery_ms: featureData.timeToDiscoveryMs,
      session_count: featureData.sessionCount,
      feature_visible: featureData.featureVisible,
      feature_relevant: featureData.featureRelevant,
      interacted_after_discovery: featureData.interactedAfterDiscovery,
      time_spent_with_feature_ms: featureData.timeSpentWithFeatureMs,
      current_energy_score: featureData.currentEnergyScore,
    }

    track('MY_ENERGY_LEVEL_FEATURE_DISCOVERED', properties)
  }

  trackEmptyStateCheckin(checkinData: {
    checkinType: 'pre_vacation' | 'post_vacation'
    selectedEnergyMode: EnergyMode
  }): void {
    this.sectionsViewed.add('suggestions')
    this.interactionCount++
    this.actionsTaken.add('empty_state_checkin')

    const properties: MyEnergyLevelEmptyStateCheckinProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      checkin_type: checkinData.checkinType,
      selected_energy_mode: checkinData.selectedEnergyMode,
      current_energy_score: this.currentEnergyScore,
      current_energy_mode: this.currentEnergyMode,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
    }

    track('MY_ENERGY_LEVEL_EMPTY_STATE_CHECKIN', properties)
  }

  // Initialize tracking on page load
  init(): void {
    // Track initial page load
    this.pageLoadTime = Date.now()

    // Set up beforeunload tracking for session end
    const beforeUnloadHandler = () => {
      this.trackSessionComplete('close_tab')
    }
    window.addEventListener('beforeunload', beforeUnloadHandler)
    this.eventHandlers.push({ event: 'beforeunload', handler: beforeUnloadHandler })

    // Set up scroll tracking
    const scrollHandler = () => {
      this.updateScrollDepth()
    }
    window.addEventListener('scroll', scrollHandler)
    this.eventHandlers.push({ event: 'scroll', handler: scrollHandler })

    // Set up engagement milestones
    const timeout30s = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 30,
      currentSection: 'unknown',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
      energyScore: this.currentEnergyScore,
      energyMode: this.currentEnergyMode,
    }), 30000) // 30 seconds
    this.timeouts.push(timeout30s)

    const timeout2m = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 120,
      currentSection: 'unknown',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
      energyScore: this.currentEnergyScore,
      energyMode: this.currentEnergyMode,
    }), 120000) // 2 minutes
    this.timeouts.push(timeout2m)
  }

  // Cleanup method to cancel all timeouts and remove event listeners
  cleanup(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout))
    this.timeouts = []

    // Remove all event listeners
    this.eventHandlers.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler)
    })
    this.eventHandlers = []
  }
}

// Create and export singleton instance
export const energyLevelTracker = new MyEnergyLevelSessionTracker()
